/**
 * Lazy A14 playlist-document ID migration.
 *
 * Legacy: users/{uid}/playlists/{rawYouTubePlaylistId} (6-field metadata).
 * Target: users/{uid}/playlists/yt:{rawYouTubePlaylistId} (Phase-4 UserPlaylist).
 *
 * Write the namespaced document first. Delete the bare document only after the
 * target is known to exist. Firestore remains the system of record; this is
 * not an IndexedDB migration and does not call YouTube.
 */
import { deleteDoc, doc, setDoc } from 'firebase/firestore';
import { userPlaylistId } from '../catalog/ids';
import { migrateOnRead } from '../catalog/migrations';
import { db } from '../firebase';

const YOUTUBE = 'youtube';
const NATIVE_PREFIX = 'rhy_pl_';

function nonemptyString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function isNativePlaylistId(id) {
  return typeof id === 'string' && id.startsWith(NATIVE_PREFIX);
}

export function isBareYouTubePlaylistDoc(id, data = {}) {
  if (!nonemptyString(id) || isNativePlaylistId(id) || id.includes(':')) {
    return false;
  }
  if (data.catalogSchemaVersion != null) return false;
  const source = nonemptyString(data.source) || YOUTUBE;
  return source === YOUTUBE;
}

export function isPhase4PlaylistDoc(data) {
  return Number.isSafeInteger(data?.catalogSchemaVersion)
    && data.catalogSchemaVersion >= 1;
}

export function namespacedYouTubePlaylistId(rawId) {
  const raw = nonemptyString(rawId);
  if (!raw) {
    throw new TypeError('providerPlaylistId is required');
  }
  if (raw.startsWith('yt:')) return raw;
  return userPlaylistId(YOUTUBE, raw);
}

export function rawYouTubePlaylistId(id, data = {}) {
  const fromData = nonemptyString(data.providerPlaylistId)
    || nonemptyString(data.playlistId);
  if (fromData && !fromData.includes(':')) return fromData;
  const raw = nonemptyString(id);
  if (!raw) return null;
  if (raw.startsWith('yt:')) return raw.slice(3);
  if (raw.includes(':')) return raw.slice(raw.indexOf(':') + 1);
  return raw;
}

export function logicalPlaylistKey(id, data = {}) {
  if (isNativePlaylistId(id) || data.kind === 'rhythme_native') {
    return `native:${id}`;
  }
  const provider = nonemptyString(data.provider)
    || nonemptyString(data.source)
    || YOUTUBE;
  const raw = rawYouTubePlaylistId(id, data) || id;
  return `${provider}:${raw}`;
}

export function toLibraryListPlaylist(id, data) {
  const value = data && typeof data === 'object' ? data : {};
  if (isPhase4PlaylistDoc(value)) {
    const rawId = rawYouTubePlaylistId(id, value) || id;
    const snapshot = value.providerSnapshot || {};
    return {
      playlistId: rawId,
      userPlaylistId: nonemptyString(value.userPlaylistId) || id,
      title: nonemptyString(value.displayName)
        || nonemptyString(snapshot.title)
        || '',
      thumbnail: nonemptyString(snapshot.thumbnailUrl) || '',
      itemCount: Number.isSafeInteger(value.knownItemCount)
        ? value.knownItemCount
        : (Number.isSafeInteger(snapshot.itemCount) ? snapshot.itemCount : 0),
      source: nonemptyString(value.provider) || YOUTUBE,
      catalogSchemaVersion: value.catalogSchemaVersion,
      kind: nonemptyString(value.kind),
      displayName: nonemptyString(value.displayName),
      lifecycle: value.lifecycle || null,
    };
  }

  return {
    playlistId: nonemptyString(value.playlistId) || id,
    title: typeof value.title === 'string' ? value.title : '',
    thumbnail: typeof value.thumbnail === 'string' ? value.thumbnail : '',
    itemCount: Number(value.itemCount) || 0,
    source: nonemptyString(value.source) || YOUTUBE,
  };
}

function preferAuthoritative(current, candidate) {
  if (!current) return candidate;
  if (!candidate) return current;
  const currentPhase4 = isPhase4PlaylistDoc(current.data);
  const candidatePhase4 = isPhase4PlaylistDoc(candidate.data);
  if (candidatePhase4 && !currentPhase4) return candidate;
  if (currentPhase4 && !candidatePhase4) return current;
  return current;
}

export function dedupeLibraryPlaylistRecords(records) {
  const chosen = new Map();
  const order = [];
  records.forEach((record) => {
    const key = logicalPlaylistKey(record.id, record.data);
    if (!chosen.has(key)) {
      chosen.set(key, record);
      order.push(key);
      return;
    }
    chosen.set(key, preferAuthoritative(chosen.get(key), record));
  });
  return order.map((key) => chosen.get(key));
}

export async function migrateBareYoutubePlaylist({
  uid,
  bareId,
  bareData = {},
  targetData = null,
  firestore = db,
  setDocFn = setDoc,
  deleteDocFn = deleteDoc,
} = {}) {
  if (!uid || !isBareYouTubePlaylistDoc(bareId, bareData)) {
    return { status: 'skipped', catalogId: null, document: targetData };
  }

  const catalogId = namespacedYouTubePlaylistId(bareId);
  const targetRef = doc(firestore, 'users', uid, 'playlists', catalogId);
  const bareRef = doc(firestore, 'users', uid, 'playlists', bareId);
  const targetHealthy = isPhase4PlaylistDoc(targetData);

  let document = targetHealthy ? targetData : null;
  if (!targetHealthy) {
    try {
      document = migrateOnRead({
        playlistId: nonemptyString(bareData.playlistId) || bareId,
        ...bareData,
      });
      await setDocFn(targetRef, document);
    } catch (error) {
      return {
        status: 'write-failed',
        catalogId,
        document: null,
        error,
      };
    }
  }

  try {
    await deleteDocFn(bareRef);
    return {
      status: targetHealthy ? 'deleted-bare' : 'migrated',
      catalogId,
      document,
    };
  } catch (error) {
    return {
      status: 'delete-failed',
      catalogId,
      document,
      error,
    };
  }
}

export async function migrateBareYoutubePlaylists(uid, records, options = {}) {
  const next = records.map((record) => ({ ...record }));
  const results = [];

  for (let index = 0; index < next.length; index += 1) {
    const record = next[index];
    if (!isBareYouTubePlaylistDoc(record.id, record.data)) continue;

    const catalogId = namespacedYouTubePlaylistId(record.id);
    const target = next.find((entry) => entry && entry.id === catalogId);
    // Sequential: each playlist is an independent document pair.
    // eslint-disable-next-line no-await-in-loop
    const result = await migrateBareYoutubePlaylist({
      uid,
      bareId: record.id,
      bareData: record.data,
      targetData: target?.data || null,
      firestore: options.firestore,
      setDocFn: options.setDocFn,
      deleteDocFn: options.deleteDocFn,
    });
    results.push(result);

    if (result.status === 'write-failed' || result.status === 'skipped') {
      continue;
    }

    if (result.document) {
      if (target) {
        target.data = result.document;
      } else {
        next.push({ id: catalogId, data: result.document });
      }
    }

    if (result.status === 'migrated' || result.status === 'deleted-bare') {
      next[index] = null;
    }
  }

  return {
    records: next.filter(Boolean),
    results,
  };
}
